import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
	Button,
	Dropdown,
	Empty,
	Menu,
	Modal,
	Spin,
	Table,
	TablePaginationConfig,
} from 'antd';
import styles from './LeaderboardsPage.module.less';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, {
	LeaderboardResetFrequency,
	LeaderboardScoreType,
	LeaderboardV2SortField,
	LeaderboardsSortOrder,
	ListLeaderboardsV2Dto,
	ListLeaderboardsV2DtoPagedResult,
	SortOrder,
} from '../../../api';
import {
	getAPISorter,
	getCellValue,
	getShowTotal,
} from '../../../utils/tableUtils';
import { EllipsisOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
	TLeaderboardResetFrequency,
	TLeaderboardV2ScoreType,
	TLeaderboardV2SortOrder,
} from '../../../utils/productUtils';
import Breadcrumb, { IRoutes } from '../../../components/Breadcrumb/Breadcrumb';
import { useSelector } from 'react-redux';
import { projectSelector } from '../../../redux/selectors/projectSelector';
import { History } from 'history';
import OmniNotification from 'components/OmniNotification';

export default function LeaderboardsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const route = useRouteMatch();
	const [leaderboards, setLeaderboards] =
		useState<ListLeaderboardsV2DtoPagedResult>();
	const [isLoading, setIsLoading] = useState(false);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const projectInfo = useSelector(projectSelector);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});

	function navigateToCreateLeaderboards() {
		history.push(`${route.url}/create`);
	}

	useEffect(() => {
		handleTableChange(pagination);
	}, []);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: `game-services`,
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('leaderboardsPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	const getActionMenu = (
		t: TFunction,
		record: ListLeaderboardsV2Dto,
		history: History,
		url: string,
	) => {
		return (
			<Menu>
				<Menu.Item
					key="edit"
					data-testid="editLeaderboardButton"
					onClick={() => {
						history.push(`${url}/${record.apiName}/edit`);
					}}
				>
					{t('leaderboardsPage.edit')}
				</Menu.Item>
				<Menu.Item
					key="delete"
					danger={true}
					onClick={() => {
						openDeleteModal(record);
					}}
				>
					{t('leaderboardsPage.delete')}
				</Menu.Item>
			</Menu>
		);
	};

	const getColumns = (t: TFunction, history: History, url: string) => {
		return [
			{
				title: t('leaderboardsPage.apiName'),
				render: (record: ListLeaderboardsV2Dto) => getCellValue(record.apiName),
				ellipsis: true,
			},
			{
				title: t('leaderboardsPage.title'),
				render: (record: ListLeaderboardsV2Dto) => getCellValue(record.title),
				ellipsis: true,
			},
			{
				title: t('leaderboardsPage.sortBy'),
				render: (record: ListLeaderboardsV2Dto) =>
					getCellValue(
						t(
							TLeaderboardV2SortOrder[
								record?.sortOrder as LeaderboardsSortOrder
							],
						),
					),
			},
			{
				title: t('leaderboardsPage.scoreType'),
				render: (record: ListLeaderboardsV2Dto) =>
					getCellValue(
						t(
							TLeaderboardV2ScoreType[
								record?.scoreType as LeaderboardScoreType
							],
						),
					),
			},
			{
				title: t('leaderboardsPage.isPublic'),
				render: (record: ListLeaderboardsV2Dto) =>
					getCellValue(
						record.isPublic
							? t('leaderboardsPage.yes')
							: t('leaderboardsPage.no'),
					),
			},
			{
				title: t('leaderboardsPage.enableSurpassedNotification'),
				render: (record: ListLeaderboardsV2Dto) =>
					getCellValue(
						record.enableSurpassedNotification
							? t('leaderboardsPage.yes')
							: t('leaderboardsPage.no'),
					),
			},
			{
				title: t('leaderboardsPage.actions'),
				render: (record: ListLeaderboardsV2Dto) => (
					<Dropdown
						overlay={getActionMenu(t, record, history, url)}
						trigger={['click']}
					>
						<EllipsisOutlined
							data-testid="leaderboardActions"
							onClick={(e) => e.preventDefault()}
						/>
					</Dropdown>
				),
				width: 150,
			},
		];
	};

	const columns = useMemo(
		() => getColumns(t, history, route.url),
		[t, history, getColumns],
	);

	const openDeleteModal = (record: ListLeaderboardsV2Dto) => {
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('leaderboardsPage.deleteModal.header')}
				</span>
			),
			okText: t('leaderboardsPage.deleteModal.okButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleDeleteLeaderboard(record);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('leaderboardsPage.deleteModal.body', {
						apiName: record.apiName,
					})}
				</span>
			),
		});
	};

	const handleDeleteLeaderboard = async (record: ListLeaderboardsV2Dto) => {
		try {
			await api.deleteLeaderboard(record.id!);
			OmniNotification.success({
				message: t('leaderboardsPage.deleteMessage'),
				description: t('leaderboardsPage.deleteDescription', {
					apiName: record.apiName,
				}),
			});
			await handleTableChange(pagination);
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} finally {
		}
	};

	const handleTableChange = async (tablePagination: TablePaginationConfig) => {
		setIsLoading(true);
		api
			.getLeaderBoardsList({
				productId: projectInfo?.productId!,
				pageNumber: (tablePagination.current as number) - 1,
				pageSize: tablePagination.pageSize as number,
				sortOrder: SortOrder.ASC,
				sortField: LeaderboardV2SortField.ApiName,
			})
			.then((leaderboards) => {
				setIsLoading(false);
				setLeaderboards(leaderboards);
				setPagination({
					...pagination,
					...tablePagination,
					total: leaderboards.totalCount || 0,
				});
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	return (
		<>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" data-testid="page-title">
							{t('leaderboardsPage.header')}
						</h2>
					</div>
					<div className={styles.buttonContainer}>
						<Button
							type="primary"
							data-testid="create-leaderboard-button"
							onClick={() => navigateToCreateLeaderboards()}
						>
							{t('leaderboardsPage.create')}
						</Button>
					</div>
				</div>
				{!leaderboards && <Spin />}
				{leaderboards && leaderboards.items?.length === 0 && (
					<div
						className={styles.emptyContainer}
						data-testid={'emptyDataDescription'}
					>
						<p>
							<span>{t('leaderboardsPage.noLeaderboards.line1')}</span>
							<br />
							<span>{t('leaderboardsPage.noLeaderboards.line2')}</span>
						</p>
					</div>
				)}
				{leaderboards && (leaderboards.items as any)?.length > 0 && (
					<div>
						<Table
							className="table"
							size="middle"
							rowKey="id"
							loading={isLoading}
							rowClassName={styles.tableRow}
							columns={columns}
							dataSource={leaderboards.items}
							onChange={handleTableChange}
							sortDirections={['descend', 'ascend', 'descend']}
							data-testid="leaderboardsTable"
							pagination={pagination}
						/>
					</div>
				)}
			</div>
		</>
	);
}
